import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { FormRenderProps } from 'react-final-form';
import equal from 'fast-deep-equal';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { FiltersModal } from 'CorePackage/FiltersModalModule/components/FiltersModal';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { ISelectInputFieldProps, SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import {
  knmFilterFormBooleanSelectOptions,
  knmFilterFormSignedSelectOptions,
} from 'KnmListPackage/KnmListPanelModule/constants/knmListFilterOptions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { ISetFilterCurrentParams } from 'CorePackage/FiltersModalModule/interfaces/ISetFilterCurrentParams';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { getUserProfileIsTest, getUserRoleIsProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IKnmStatusSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmStatusSelectOption';
import { ICountrySelectOption } from 'CorePackage/CatalogsModule/interfaces/ICountrySelectOption';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import {
  makeGetCatalogSelectOptions,
  getCatalogCountriesSelectOptions,
  getCatalogKnmYearsSelectOptions,
  getUserKnoSelectOptions,
  getCatalogPmStatusesSelectOptions,
  getCatalogsTerritorialUnitsSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IYearSelectOption } from 'CorePackage/MainModule/interfaces/IYearSelectOption';
import { IGenericReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/IGenericReasonSelectOption';
import { IDictionaryTargetEntitySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryTargetEntitySelectOption';
import { IDictionarySubjectTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectTypeSelectOption';
import { IDictionarySubjectKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectKindSelectOption';
import { IDictionarySubjectSubKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectSubKindSelectOption';
import { IDictionaryRiskCategorySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryRiskCategorySelectOption';
import { IDictionaryDangerClassSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryDangerClassSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IFilterResult } from 'APIPackage/MainModule/interfaces/IFilterResult';
import { preventionGetKnms } from 'PreventionPackage/MainModule/actions/preventionActions';
import { IProfKind } from 'APIPackage/DictionariesModule/interfaces/IProfKind';
import {
  getDictionariesAllProfKindsSelectOptions,
  getDictionariesKnoOrganizationsSelectOptions,
  makeGetDictionariesTargetEntitySelectOptions,
  makeGetDictionariesSubjectTypesMultiSelectOptions,
  makeGetDictionariesSubjectKindsMultiSelectOptions,
  makeGetDictionariesSubjectSubKindsMultiSelectOptions,
  makeGetDictionariesSupervisionTypesExtendedSelectOptions,
  getDictionariesRiskCategoriesData,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';

import { convertFilterDataToControlOrganizationsIds } from '../common/convertFilterDataToControlOrganizationsIds';
import { convertFilterDataToSupervisionTypesIds } from '../common/convertFilterDataToSupervisionTypesIds';
import { initialPmListFilter } from '../constants/initialPmListFilter';
import {
  IPmListFilterSetCurrentParams,
  pmListFilterCreate,
  pmListFilterGetData,
  pmListFilterInit,
  pmListFilterRemove,
  pmListFilterSetCurrent,
  pmListFilterSetCurrentControlOrganizations,
  pmListFilterSetCurrentSupervisionTypes,
  pmListFilterSetMeta,
  pmListFilterSetSwitcher,
  pmListFilterUpdate,
} from '../actions/pmListFilterActions';
import styles from './PmListFiltersModal.module.scss';
import { IPmListFiltersForm } from '../interfaces/IPmListFiltersForm';
import { IPmListFilterExtraField } from '../interfaces/IPmListFilterExtraField';
import { IPmListFilter } from '../interfaces/IPmListFilter';
import { mapFilterAdditionalFields } from '../common/mapFilterAdditionalFields';
import {
  initialPmListAdditionalFields as initialPmListAdditionalFieldsConstant,
  isTestAdditionalField,
} from '../constants/initialPmListAdditionalFields';
import { IPmListFilterWithMeta } from '../interfaces/IPmListFilterWithMeta';
import {
  getPmListCurrentControlOrganizations,
  getPmListCurrentSupervisionTypes,
  getPmListFilterCurrentFilter,
  getPmListFilterCurrentId,
  getPmListFilterFilters,
} from '../selectors/pmListFilterSelectors';
import { createDictionaryRiskCategorySelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryRiskCategorySelectOption';
import { createDictionaryDangerClassSelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryDangerClassSelectOption';
import { erknmPublishedStatusesSelectOptions } from '../../../CorePackage/MainModule/constants/erknmPublishedStatuses';
import { ITerritorialUnitSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/ITerritorialUnitSelectOption';

const mapFilter = (filter: IPmListFilter, filterName: string): IPmListFilter => {
  return {
    ...filter,
    name: filterName,
  };
};

interface IPmListFiltersModalProps {
  visible: boolean;
  onClose: () => void;
}

interface IPmListFiltersModalStoreProps {
  yearsSelectOptions: IYearSelectOption[];
  userKnoOrganisationsSelectOptions: IUserOrganisationSelectOption[] | undefined;
  roleProsecutor: boolean;
  filters: IPmListFilterWithMeta[];
  currentFilter: IPmListFilter | undefined;
  activeFilterId: number | undefined;
  pmStatusesSelectOptions: IKnmStatusSelectOption[];
  countriesSelectOptions: ICountrySelectOption[];
  reasonsSelectOptions: IGenericReasonSelectOption[];
  targetEntitiesSelectOptions: IDictionaryTargetEntitySelectOption[] | undefined;
  subjectTypesSelectOptions: IDictionarySubjectTypeSelectOption[] | undefined;
  subjectKindsSelectOptions: IDictionarySubjectKindSelectOption[] | undefined;
  subjectSubKindsSelectOptions: IDictionarySubjectSubKindSelectOption[] | undefined;
  riskCategoriesSelectOptions: IDictionaryRiskCategorySelectOption[] | undefined;
  dangerClassesSelectOptions: IDictionaryDangerClassSelectOption[] | undefined;
  currentYearSelectOption: IYearSelectOption | undefined;
  erknmSupervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] | undefined;
  kindsSelectOptions: IDictionarySelectOption<IProfKind>[] | undefined;
  erknmOrganisationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  territorialUnitsSelectOptions: ITerritorialUnitSelectOption[];
  isUserTest: boolean;
}

const PmListFiltersModalWithoutStore = ({
  visible,
  onClose,
  activeFilterId,
  currentFilter,
  filters,
  currentYearSelectOption,
  pmStatusesSelectOptions,
  countriesSelectOptions,
  reasonsSelectOptions,
  targetEntitiesSelectOptions,
  subjectTypesSelectOptions,
  subjectKindsSelectOptions,
  subjectSubKindsSelectOptions,
  riskCategoriesSelectOptions,
  dangerClassesSelectOptions,
  roleProsecutor,
  userKnoOrganisationsSelectOptions,
  yearsSelectOptions,
  erknmSupervisionTypesSelectOptions,
  erknmOrganisationsSelectOptions,
  kindsSelectOptions,
  territorialUnitsSelectOptions,
  isUserTest,
}: IPmListFiltersModalProps & IPmListFiltersModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useUnmount(() => {
    dispatch(pmListFilterSetMeta([]));
  });

  const getPmListData = useCallback(() => dispatch(preventionGetKnms()), [dispatch]);
  const getPmListFilterData = useCallback((id: IFilterResult['id']) => dispatch(pmListFilterGetData(id)), [dispatch]);
  const setPmListFilterCurrent = useCallback(
    (params: IPmListFilterSetCurrentParams) => dispatch(pmListFilterSetCurrent(params)),
    [dispatch],
  );
  const setPmListFilterSwitcher = useCallback((value: boolean) => dispatch(pmListFilterSetSwitcher(value)), [dispatch]);
  const createPmListFilter = useCallback((data: IPmListFilter) => dispatch(pmListFilterCreate(data)), [dispatch]);
  const updatePmListFilter = useCallback(
    (id: IFilterResult['id'], data: IPmListFilter) => dispatch(pmListFilterUpdate(id, data)),
    [dispatch],
  );
  const removePmListFilter = useCallback((id: IFilterResult['id']) => dispatch(pmListFilterRemove(id)), [dispatch]);

  const setCurrentControlOrganizations = useCallback(
    (ids: string[] | undefined) => dispatch(pmListFilterSetCurrentControlOrganizations(ids || [])),
    [dispatch],
  );

  const setCurrentSupervisionTypes = useCallback(
    (ids: string[] | undefined) => dispatch(pmListFilterSetCurrentSupervisionTypes(ids || [])),
    [dispatch],
  );

  const initialPmListAdditionalFields = useMemo(() => {
    const result = [...initialPmListAdditionalFieldsConstant];

    if (isUserTest && !initialPmListAdditionalFieldsConstant.find(i => i.id === isTestAdditionalField.id)) {
      result.push(isTestAdditionalField);
    }

    return result;
  }, []);

  const mapper = useMemo(() => mapFilterAdditionalFields(isUserTest), [isUserTest]);

  const defaultKnoOrganisationSelectOption = useMemo(
    () =>
      erknmOrganisationsSelectOptions?.find(
        item =>
          item.id.toString() ===
          userKnoOrganisationsSelectOptions
            ?.find(userKnoOrganisationsSelectOption => userKnoOrganisationsSelectOption.byDefault)
            ?.id.toString(),
      ),
    [erknmOrganisationsSelectOptions, userKnoOrganisationsSelectOptions],
  );

  const defaultTerritorialUnitSelectOption = useMemo(
    () => territorialUnitsSelectOptions.find(selectOption => selectOption.isDefault),
    [territorialUnitsSelectOptions],
  );

  const memoizedInitialPmListFilter: IPmListFilter = useMemo(
    () => ({
      name: 'Новый фильтр',
      years: currentYearSelectOption ? [currentYearSelectOption] : [],
      ...(defaultTerritorialUnitSelectOption && { domains: [defaultTerritorialUnitSelectOption] }),
      includeDomainChild: false,
      ignoreKno: false,
      includeChildKno: false,
      ...(defaultKnoOrganisationSelectOption && { controllingOrganizations: [defaultKnoOrganisationSelectOption] }),
      ...(kindsSelectOptions && kindsSelectOptions.length && { kinds: kindsSelectOptions }),
    }),
    [
      currentYearSelectOption,
      defaultKnoOrganisationSelectOption,
      kindsSelectOptions,
      defaultTerritorialUnitSelectOption,
    ],
  );

  const [formValues, setFormValues] = useState<IPmListFiltersForm>(initialPmListFilter);

  useEffect(() => {
    dispatch(pmListFilterInit());
  }, [dispatch]);

  const memoizedFilterSelectFields: Omit<IPmListFilterExtraField, 'isSelected' | 'name'>[] = useMemo(
    () => [
      {
        id: 'supervisionType',
        options: erknmSupervisionTypesSelectOptions,
      },
      {
        id: 'deletedOnly',
        options: knmFilterFormBooleanSelectOptions,
      },
      {
        id: 'deletedInclude',
        options: knmFilterFormBooleanSelectOptions,
      },
      {
        id: 'statuses',
        options: pmStatusesSelectOptions,
      },
      {
        id: 'subjectCountries',
        options: countriesSelectOptions,
      },
      {
        id: 'hasObjectionWarning',
        options: knmFilterFormBooleanSelectOptions,
      },
      {
        id: 'reasons',
        options: reasonsSelectOptions,
      },
      {
        id: 'subjectTypes',
        options: targetEntitiesSelectOptions,
      },
      {
        id: 'objectKinds',
        options: subjectKindsSelectOptions,
      },
      {
        id: 'objectSubKinds',
        options: subjectSubKindsSelectOptions,
      },
      {
        id: 'objectRiskCategory',
        options: riskCategoriesSelectOptions,
      },
      {
        id: 'objectDangerClass',
        options: dangerClassesSelectOptions,
      },
      {
        id: 'isHasCollaboratingOrganization',
        options: knmFilterFormBooleanSelectOptions,
      },
      {
        id: 'signed',
        options: knmFilterFormSignedSelectOptions,
      },
      {
        id: 'pubStatuses',
        options: erknmPublishedStatusesSelectOptions,
      },
      {
        id: 'objectTypes',
        options: subjectTypesSelectOptions,
      },
      {
        id: 'isTest',
        options: knmFilterFormBooleanSelectOptions,
      },
    ],
    [
      erknmSupervisionTypesSelectOptions,
      pmStatusesSelectOptions,
      countriesSelectOptions,
      reasonsSelectOptions,
      targetEntitiesSelectOptions,
      subjectTypesSelectOptions,
      subjectKindsSelectOptions,
      subjectSubKindsSelectOptions,
      riskCategoriesSelectOptions,
      dangerClassesSelectOptions,
    ],
  );

  const [additionalFields, setAdditionalFields] = useState(initialPmListAdditionalFields);

  const onTemplateCreate = useCallback(() => {
    const ids = convertFilterDataToControlOrganizationsIds(
      erknmOrganisationsSelectOptions,
      memoizedInitialPmListFilter?.controllingOrganizations,
    );

    setCurrentControlOrganizations(ids || []);
    setCurrentSupervisionTypes(ids || []);
    const newData = {
      ...initialPmListFilter,
      ...memoizedInitialPmListFilter,
    };

    setFormValues(newData);
    const filter = mapFilter(newData, 'Новый фильтр');
    const filterData: ISetFilterCurrentParams<IPmListFilter> = {
      current: { ...filter, isTemplateFirstCreate: true },
      currentId: undefined,
    };

    setPmListFilterCurrent(filterData);
    setAdditionalFields(initialPmListAdditionalFields);
  }, [
    setCurrentControlOrganizations,
    setCurrentSupervisionTypes,
    erknmOrganisationsSelectOptions,
    initialPmListAdditionalFields,
    memoizedInitialPmListFilter,
    setPmListFilterCurrent,
  ]);

  useEffect(() => {
    const newData = {
      ...initialPmListFilter,
      ...memoizedInitialPmListFilter,
      isTemplateFirstCreate: true,
    };

    if (!equal(formValues, newData) && activeFilterId === undefined && formValues.isTemplateFirstCreate) {
      onTemplateCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterId, memoizedInitialPmListFilter]);

  const removeAdditionalField = (name: IPmListFilterExtraField['id']) => {
    setAdditionalFields(prev =>
      prev.map(f => {
        if (f.id === name) {
          return {
            ...f,
            isSelected: false,
          };
        }

        return f;
      }),
    );
  };

  function renderFormContent({ values, form }: FormRenderProps<IPmListFiltersForm>) {
    const makeRemoveFilterHandler = (name: IPmListFilterExtraField['id']) => () => {
      removeAdditionalField(name);
      if (name === 'startDateBetween') {
        form.change('startDateBetweenStart');
        form.change('startDateBetweenEnd');
      } else {
        form.change(name);
      }
    };

    function handleChangeControllingOrganizations(value: IDictionarySelectOption<IKnoOrganizationDictionary>[]) {
      const ids = convertFilterDataToControlOrganizationsIds(erknmOrganisationsSelectOptions, value);

      dispatch(pmListFilterSetCurrentControlOrganizations(ids || []));
    }

    function handleChangeSupervisionTypes(value: IDictionarySupervisionTypeSelectOption) {
      const ids = convertFilterDataToSupervisionTypesIds(erknmSupervisionTypesSelectOptions, [value]);

      dispatch(pmListFilterSetCurrentSupervisionTypes(ids || []));
    }

    function isSelectInputFieldMultiClearableEqual(
      a: ISelectOption[] | null | undefined,
      b: ISelectOption[] | null | undefined,
    ) {
      const newA = a?.length === 0 ? undefined : a;
      const newB = b?.length === 0 ? undefined : b;

      return equal(newA, newB);
    }

    function validateControllingOrganizationsSelect(value: ISelectInputFieldProps['value']) {
      if (form.getState().values.ignoreKno) {
        return undefined;
      }

      return makeRequired('Поле не может быть пустым')(value);
    }

    function checkSameOptions(option: ISelectOption, selectValue: ISelectOption[]) {
      return selectValue.some(item => equal(option, item));
    }

    const primaryContent = (
      <>
        <Row>
          <FieldBlock label="Год проведения ПМ (На основе даты проведения ПМ)" labelFontSize="small">
            <SelectInputField
              id="years"
              name="years"
              options={yearsSelectOptions}
              isSearchable
              isMulti
              isClearable={false}
              isEqual={equal}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock label="Территориальная единица" labelFontSize="small">
            <Row>
              <FieldBlock>
                <SelectInputField
                  id="domains"
                  name="domains"
                  options={territorialUnitsSelectOptions}
                  isSearchable
                  isMulti
                  isEqual={isSelectInputFieldMultiClearableEqual}
                  validate={makeRequired('Поле не может быть пустым')}
                />
              </FieldBlock>
            </Row>
            <CheckboxField
              id="includeDomainChild"
              name="includeDomainChild"
              label="Включая подчиненные организации"
              theme="system"
              type="checkbox"
            />
          </FieldBlock>
        </Row>

        <Row border="bottom">
          <FieldBlock label="Вид ПМ" labelFontSize="small">
            <SelectInputField
              id="kinds"
              name="kinds"
              options={kindsSelectOptions}
              isMulti
              isClearable={false}
              isEqual={equal}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
        </Row>
        <FieldBlock label="Наименование органа контроля (надзора)" labelFontSize="small">
          <Row>
            <FieldBlock>
              <SelectInputField
                id="controllingOrganizations"
                name="controllingOrganizations"
                options={erknmOrganisationsSelectOptions}
                disabled={values.ignoreKno}
                isMulti
                isSearchable
                isEqual={isSelectInputFieldMultiClearableEqual}
                isOptionSelected={checkSameOptions}
                validate={validateControllingOrganizationsSelect}
                onChange={handleChangeControllingOrganizations}
              />
            </FieldBlock>
          </Row>
          <Row>
            <CheckboxField
              id="includeChildKno"
              name="includeChildKno"
              label="Включая подчиненные организации"
              theme="system"
              type="checkbox"
            />
          </Row>
          {roleProsecutor && (
            <CheckboxField
              id="ignoreKno"
              name="ignoreKno"
              label="Не учитывать орган контроля"
              theme="system"
              type="checkbox"
            />
          )}
        </FieldBlock>
      </>
    );

    const { controllingOrganizations, supervisionType } = form.getState().values;
    const areKnoAndSupervisionSpecified = controllingOrganizations?.length && supervisionType;

    const secondaryContent = (
      <>
        {additionalFields
          .filter(f => f.isSelected)
          .map((field, index) => (
            <Row
              key={field.id}
              border={index < additionalFields.filter(f => f.isSelected).length - 1 ? 'bottom' : undefined}
            >
              <FieldBlock
                label={field.name}
                labelFontSize="small"
                type="flex-label"
                button={<CloseButton onClick={makeRemoveFilterHandler(field.id)} />}
              >
                {field.type === 'checkbox' && (
                  <CheckboxField id={field.id} name={field.id} theme="system" type="checkbox" />
                )}
                {field.type === 'text' && (
                  <TextInputField name={field.id} type="text" validate={makeRequired('Поле не может быть пустым')} />
                )}
                {field.type === 'date' && field.id === 'startDateBetween' && (
                  <div className={styles.FilterFormDateGroup}>
                    С&nbsp;
                    <div className={styles.DateFieldWrapper}>
                      <DatePickerField
                        className={styles.FilterFormGroupDatePickersItem}
                        name="startDateBetweenStart"
                        opensOnTop
                        isEqual={equal}
                        validate={makeRequired('Поле не может быть пустым')}
                      />
                    </div>
                    по&nbsp;
                    <div className={styles.DateFieldWrapper}>
                      <DatePickerField
                        className={styles.FilterFormGroupDatePickersItem}
                        name="startDateBetweenEnd"
                        opensOnTop
                        isEqual={equal}
                        validate={makeRequired('Поле не может быть пустым')}
                      />
                    </div>
                  </div>
                )}
                {(field.id === 'objectTypes' || field.id === 'objectKinds' || field.id === 'objectSubKinds') &&
                  !areKnoAndSupervisionSpecified && <label>Выберите КНО и вид контроля</label>}
                {field.type === 'select' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    options={memoizedFilterSelectFields.find(item => item.id === field.id)?.options}
                    isSearchable={field.isSearchable}
                    isMulti={field.isMulti}
                    isClearable={field.isClearable || false}
                    isEqual={field.isMulti && field.isClearable ? isSelectInputFieldMultiClearableEqual : equal}
                    validate={makeRequired('Поле не может быть пустым')}
                    onChange={field.id === 'supervisionType' ? handleChangeSupervisionTypes : undefined}
                    getOptionValue={v => v.value.dictVersionId || v.value}
                  />
                )}
              </FieldBlock>
            </Row>
          ))}
      </>
    );

    return {
      primaryContent,
      secondaryContent,
    };
  }

  return (
    <FiltersModal<IPmListFiltersForm, IPmListFilter>
      visible={visible}
      initialFormValues={formValues}
      additionalFields={additionalFields}
      filters={filters}
      currentFilter={currentFilter}
      activeFilterId={activeFilterId}
      renderFormContent={renderFormContent}
      mapFilter={mapFilter}
      mapFilterAdditionalFields={mapper}
      onClose={onClose}
      onTemplateCreate={onTemplateCreate}
      setFormValues={setFormValues}
      setAdditionalFields={setAdditionalFields}
      getData={getPmListData}
      getFilterData={getPmListFilterData}
      setFilterCurrent={setPmListFilterCurrent}
      setFilterSwitcher={setPmListFilterSwitcher}
      createFilter={createPmListFilter}
      updateFilter={updatePmListFilter}
      removeFilter={removePmListFilter}
    />
  );
};

const mapStateToProps = (state: IRootState): IPmListFiltersModalStoreProps => {
  const pmStatusesSelectOptions = getCatalogPmStatusesSelectOptions(state);
  const countriesSelectOptions = getCatalogCountriesSelectOptions(state);
  const knmYearsSelectOptions: IYearSelectOption[] = getCatalogKnmYearsSelectOptions(state);

  const currentYear = new Date().getFullYear();
  const nextYear = String(currentYear + 1);
  const currentYearSelectOption = knmYearsSelectOptions.find(
    selectOption => selectOption.value === String(currentYear),
  );

  const nextYearSelectOption = knmYearsSelectOptions.find(selectOption => selectOption.value === nextYear);
  const yearsSelectOptions = nextYearSelectOption
    ? knmYearsSelectOptions
    : [...knmYearsSelectOptions, { id: nextYear, label: nextYear, value: nextYear }];

  const controllingOrganizations = getPmListCurrentControlOrganizations(state);
  const supervisionTypes = getPmListCurrentSupervisionTypes(state);

  const getDictionariesSubjectTypesMultiSelectOptions = makeGetDictionariesSubjectTypesMultiSelectOptions();
  const getDictionariesSubjectKindsMultiSelectOptions = makeGetDictionariesSubjectKindsMultiSelectOptions();
  const getDictionariesSubjectSubKindsMultiSelectOptions = makeGetDictionariesSubjectSubKindsMultiSelectOptions();
  const areKnoAndSupervisionSpecified = controllingOrganizations.length && supervisionTypes.length;
  const subjectTypesSelectOptions = areKnoAndSupervisionSpecified
    ? getDictionariesSubjectTypesMultiSelectOptions(state, controllingOrganizations, supervisionTypes)
    : [];
  const subjectKindsSelectOptions = areKnoAndSupervisionSpecified
    ? getDictionariesSubjectKindsMultiSelectOptions(state, controllingOrganizations, supervisionTypes)
    : [];
  const subjectSubKindsSelectOptions = areKnoAndSupervisionSpecified
    ? getDictionariesSubjectSubKindsMultiSelectOptions(state, controllingOrganizations, supervisionTypes)
    : [];

  const getDictionariesSupervisionTypesSelectOptions = makeGetDictionariesSupervisionTypesExtendedSelectOptions();
  const getDictionariesGenericReasonsSelectOptions = makeGetCatalogSelectOptions(
    CommonCatalogsNamesEnum.GENERIC_REASONS,
  );
  const getDictionariesTargetEntitySelectOptions = makeGetDictionariesTargetEntitySelectOptions();

  const riskAndDangerCategories = getDictionariesRiskCategoriesData(
    state,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
  );
  const riskCategoriesSelectOptions = riskAndDangerCategories?.values
    .filter(item => !item.value.code?.startsWith('0'))
    .map(item => createDictionaryRiskCategorySelectOption(riskAndDangerCategories?.idDictionary, item));
  const dangerClassesSelectOptions = riskAndDangerCategories?.values
    .filter(item => item.value.code?.startsWith('0'))
    .map(item => createDictionaryDangerClassSelectOption(riskAndDangerCategories?.idDictionary, item));

  return {
    activeFilterId: getPmListFilterCurrentId(state),
    currentFilter: getPmListFilterCurrentFilter(state),
    filters: getPmListFilterFilters(state),
    pmStatusesSelectOptions,
    countriesSelectOptions,
    reasonsSelectOptions: getDictionariesGenericReasonsSelectOptions(state),
    targetEntitiesSelectOptions: getDictionariesTargetEntitySelectOptions(state, undefined)?.filter(
      targetEntityOption => targetEntityOption.code !== null,
    ),
    subjectTypesSelectOptions,
    subjectKindsSelectOptions,
    subjectSubKindsSelectOptions,
    riskCategoriesSelectOptions,
    dangerClassesSelectOptions,
    roleProsecutor: getUserRoleIsProsecutor(state),
    userKnoOrganisationsSelectOptions: getUserKnoSelectOptions(state),
    yearsSelectOptions,
    currentYearSelectOption,
    erknmOrganisationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
    erknmSupervisionTypesSelectOptions: getDictionariesSupervisionTypesSelectOptions(
      state,
      controllingOrganizations || [],
      false,
      false,
    ),
    kindsSelectOptions: getDictionariesAllProfKindsSelectOptions(state),
    territorialUnitsSelectOptions: getCatalogsTerritorialUnitsSelectOptions(state),
    isUserTest: getUserProfileIsTest(state),
  };
};

export const PmListFiltersModal = connect(mapStateToProps)(PmListFiltersModalWithoutStore);
